import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiPlus, FiX } from "react-icons/fi";
import { BOARD_DISPLAY_NAME } from "@/config/browserBoard";
import {
  getPostAttachmentIdentity,
  reconcileAttachmentIdentities,
} from "@/entities/attachment";
import {
  getPostAttachments,
  getPostPlainBody,
  type Post,
  postNo,
} from "@/entities/post/types";
import type { Thread, ThreadChunkPost } from "@/entities/thread/types";
import { getImageUrl, isR18ThreadTagName } from "@/entities/thread/utils";
import { useBoardListPage } from "@/features/board/hooks/useBoardListPage";
import {
  type BoardThreadTail,
  LIST_PAGE_LIMIT,
  useBoardThreadTails,
} from "@/features/board/hooks/useBoardThreads";
import { isR18ImageHidden } from "@/features/board/utils/r18Spoiler";
import { CatalogSortModal } from "@/features/catalog/components/modals/CatalogSortModal";
import { TagFilterModal } from "@/features/catalog/components/modals/TagFilterModal";
import { CatalogPagination } from "@/features/catalog/components/navigation/CatalogPagination";
import { CatalogThreadLink } from "@/features/catalog/components/navigation/CatalogThreadLink";
import { DesktopCatalogToolbar } from "@/features/catalog/components/navigation/DesktopCatalogToolbar";
import { MobileCatalogFooter } from "@/features/catalog/components/navigation/MobileCatalogFooter";
import {
  getThreadLinkAttributes,
  useFilteredThreads,
} from "@/features/catalog/hooks/useFilteredThreads";
import { useThreads } from "@/features/catalog/hooks/useThreads";
import { useCatalogStore } from "@/features/catalog/stores/catalogStore";
import {
  DesktopHistorySidebar,
  type DesktopHistorySidebarHandle,
} from "@/features/history/components/DesktopHistorySidebar";
import { DesktopSettingsModal } from "@/features/settings/components/desktop/DesktopSettingsModal";
import { useSettingsStore } from "@/features/settings/hooks";
import { DesktopThreadCreatePanel } from "@/features/thread/components/desktop/DesktopThreadCreatePanel";
import { ThreadCreateModal } from "@/features/thread/components/modals/ThreadCreateModal";
import { useOpenPanelOnFileDrag } from "@/features/thread/hooks/useOpenPanelOnFileDrag";
import { useThreadCreateModalStore } from "@/features/thread/stores/threadCreateModalStore";
import { useIsDesktop } from "@/shared/hooks";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { useManualRefresh } from "@/shared/hooks/useManualRefresh";
import { hasLinkModifierKey } from "@/shared/lib/keyboardAccessibility";
import { Message, RefreshStatus } from "@/shared/ui/feedback";
import { QueryStateBoundary } from "@/shared/ui/feedback/QueryStateBoundary";

interface BoardQueryStateProps {
  children: React.ReactNode;
  error: unknown;
  hasData: boolean;
  isLoading: boolean;
  isRetrying: boolean;
  onRetry: () => void;
}

export const BoardQueryState: React.FunctionComponent<BoardQueryStateProps> = ({
  children,
  error,
  hasData,
  isLoading,
  isRetrying,
  onRetry,
}: BoardQueryStateProps) => (
  <QueryStateBoundary
    error={error}
    hasData={hasData}
    isLoading={isLoading}
    isRetrying={isRetrying}
    message="スレッド一覧の読み込みに失敗しました"
    onRetry={onRetry}
  >
    {children}
  </QueryStateBoundary>
);

export const BoardPage: React.FunctionComponent = () => {
  useDocumentTitle(`リストモード - ${BOARD_DISPLAY_NAME}`);
  const currentSort = useCatalogStore((state) => state.currentSort);
  const selectSortMethod = useCatalogStore((state) => state.selectSortMethod);
  const autoReload = useCatalogStore((state) => state.autoReload);
  const setAutoReload = useCatalogStore((state) => state.setAutoReload);
  const openInNewTab = useCatalogStore((state) => state.openInNewTab);
  const isThreadCreateOpen = useThreadCreateModalStore((state) => state.isOpen);
  const openThreadCreate = useThreadCreateModalStore((state) => state.open);
  const closeThreadCreate = useThreadCreateModalStore((state) => state.close);
  const showR18 = useSettingsStore((state) => state.showR18);
  const openThreadCreateOnFileDrag = useOpenPanelOnFileDrag(
    isThreadCreateOpen,
    openThreadCreate,
  );
  const desktopThreadCreateButtonRef = useRef<HTMLButtonElement>(null);
  const desktopHistorySidebarRef = useRef<DesktopHistorySidebarHandle>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [isTagFilterOpen, setIsTagFilterOpen] = useState(false);
  const navigate = useNavigate();
  // リストモードのページはカタログモードのcatalogStore.pageと共有しない
  // (共有すると片方のページ送りがもう片方に持ち越されるため)。sortだけは
  // モード間で並び順を保つ意図で共有を維持する(#42レビュー指摘)。
  // カタログとは独立したページ番号を維持しつつ、並び順・検索・タグ条件が
  // 変わった場合は、絞り込み前のページ番号を持ち越さず1ページ目へ戻す。
  const [page, setPage] = useBoardListPage(currentSort);
  const { data, isLoading, isFetching, error, refetch } = useThreads({
    page,
    limit: LIST_PAGE_LIMIT,
  });
  const isDesktop = useIsDesktop();
  const [prevIsDesktop, setPrevIsDesktop] = useState(isDesktop);
  if (isDesktop !== prevIsDesktop) {
    setPrevIsDesktop(isDesktop);
    if (isDesktop) {
      setIsSortOpen(false);
      setIsTagFilterOpen(false);
    } else {
      setIsSettingsOpen(false);
    }
  }

  const { isManualRefreshing, refreshManually } = useManualRefresh(
    refetch,
    isFetching,
  );

  useEffect(() => {
    return (): void => closeThreadCreate();
  }, [closeThreadCreate]);

  const closeDesktopThreadCreatePanel = useCallback((): void => {
    closeThreadCreate();
    window.requestAnimationFrame(() => {
      desktopThreadCreateButtonRef.current?.focus();
    });
  }, [closeThreadCreate]);

  const threads = useFilteredThreads(data?.threads);
  const tails = useBoardThreadTails(threads.map((thread) => thread.id));

  const pagination = (
    <CatalogPagination
      page={page}
      hasNextPage={data?.pagination.hasNextPage ?? false}
      isFetching={isFetching}
      onChange={setPage}
      ariaLabel="リストのページ移動"
    />
  );

  const threadList = (
    <BoardThreadList
      threads={threads}
      tails={tails.tails}
      tailsIsFetching={tails.isFetching}
      tailsHasError={tails.hasError}
      openInNewTab={openInNewTab}
      showR18={showR18}
    />
  );

  if (isDesktop) {
    return (
      <section
        aria-labelledby="board-page-title"
        className="desktop-catalog-page"
      >
        <h1 id="board-page-title" className="sr-only">
          リストモード
        </h1>
        <BoardQueryState
          error={error}
          hasData={data !== undefined}
          isLoading={isLoading}
          isRetrying={isFetching}
          onRetry={(): void => void refetch()}
        >
          <DesktopCatalogToolbar
            activeMode="board"
            tagFilterThreads={data?.threads ?? []}
            currentSort={currentSort}
            selectSortMethod={selectSortMethod}
            autoReload={autoReload}
            setAutoReload={setAutoReload}
            isFetching={isFetching}
            onRefresh={(): void => void refreshManually()}
            onToggleSidebar={(): void =>
              desktopHistorySidebarRef.current?.toggle()
            }
            onScrollToTop={(): void => window.scrollTo({ top: 0 })}
            onScrollToBottom={(): void =>
              window.scrollTo({ top: document.documentElement.scrollHeight })
            }
            onSettingsClick={(
              event: React.MouseEvent<HTMLAnchorElement>,
            ): void => {
              if (hasLinkModifierKey(event)) return;
              event.preventDefault();
              setIsSettingsOpen(true);
            }}
            onOpenThreadCreate={openThreadCreate}
          />
          <RefreshStatus isRefreshing={isManualRefreshing} />
          <p aria-hidden="true" className="desktop-mode-title">
            リストモード
          </p>
          {/*
          CatalogPageのPC版構造(desktop-catalog-grid-wrap)に合わせ、
          board-page(width/padding付き)をここで使わない。desktop-catalog-page
          に board-page をネストするとpaddingが二重にかかり幅も不一致になる
          ため(#42レビュー指摘)。
        */}
          <section
            className="desktop-catalog-grid-wrap"
            aria-label="スレッド一覧"
          >
            {threadList}
          </section>
          {pagination}
          <DesktopHistorySidebar
            ref={desktopHistorySidebarRef}
            showClosedHandle={false}
          />
          <DesktopThreadCreatePanel
            isOpen={isThreadCreateOpen}
            onClose={closeDesktopThreadCreatePanel}
          />
          <DesktopSettingsModal
            isOpen={isSettingsOpen}
            onClose={(): void => setIsSettingsOpen(false)}
          />
          <button
            ref={desktopThreadCreateButtonRef}
            className="desktop-catalog-fab"
            type="button"
            aria-expanded={isThreadCreateOpen}
            onClick={
              isThreadCreateOpen
                ? closeDesktopThreadCreatePanel
                : openThreadCreate
            }
            onDragEnter={openThreadCreateOnFileDrag}
          >
            {isThreadCreateOpen ? (
              <FiX aria-hidden="true" />
            ) : (
              <FiPlus aria-hidden="true" />
            )}
            {isThreadCreateOpen ? "閉じる" : "新規スレ"}
          </button>
        </BoardQueryState>
      </section>
    );
  }

  return (
    <section aria-labelledby="board-page-title">
      <h1 id="board-page-title" className="sr-only">
        リストモード
      </h1>
      <BoardQueryState
        error={error}
        hasData={data !== undefined}
        isLoading={isLoading}
        isRetrying={isFetching}
        onRetry={(): void => void refetch()}
      >
        <section className="board-page pb-20" aria-label="スレッド一覧">
          <p
            aria-hidden="true"
            className="my-2 text-xl font-bold text-foreground"
          >
            リストモード
          </p>
          {threadList}
          {pagination}
        </section>
        <MobileCatalogFooter
          ariaLabel="リストメニュー"
          isRefreshing={isFetching}
          onOpenSort={(): void => setIsSortOpen(true)}
          onOpenTagFilter={(): void => setIsTagFilterOpen(true)}
          onRefresh={(): void => void refreshManually()}
          primaryAction={{
            icon: FiPlus,
            label: "新規スレッド",
            onClick: openThreadCreate,
          }}
        />
        {isSortOpen && (
          <CatalogSortModal
            isOpen
            initialDisplayType="list"
            currentSort={currentSort}
            selectSortMethod={selectSortMethod}
            onShowCatalog={(): void => {
              void navigate({ to: "/" });
            }}
            onShowList={(): void => undefined}
            onClose={(): void => setIsSortOpen(false)}
          />
        )}
        <TagFilterModal
          isOpen={isTagFilterOpen}
          threads={data?.threads ?? []}
          onClose={(): void => setIsTagFilterOpen(false)}
        />
        <ThreadCreateModal
          isOpen={isThreadCreateOpen}
          onClose={closeThreadCreate}
        />
      </BoardQueryState>
    </section>
  );
};

interface BoardThreadListProps {
  threads: Thread[];
  tails: Map<string, BoardThreadTail>;
  tailsIsFetching: boolean;
  tailsHasError: boolean;
  openInNewTab: boolean;
  showR18: boolean;
}

export const BoardThreadList: React.FunctionComponent<BoardThreadListProps> = ({
  threads,
  tails,
  tailsIsFetching,
  tailsHasError,
  openInNewTab,
  showR18,
}: BoardThreadListProps) => {
  const threadLinkAttributes = getThreadLinkAttributes(openInNewTab);

  return (
    <>
      {tailsIsFetching && <p aria-live="polite">最新レスを取得中...</p>}
      {tailsHasError && (
        <Message variant="error">
          一部スレッドの最新レスを取得できませんでした
        </Message>
      )}
      {threads.length === 0 ? (
        <Message variant="info">スレッドはありません</Message>
      ) : (
        threads.map((thread) => {
          const isR18Thread = thread.tags.some((tag) =>
            isR18ThreadTagName(tag.name),
          );
          return (
            <article key={thread.id} className="board-thread">
              <CatalogThreadLink
                thread={thread}
                className="board-thread-link"
                {...threadLinkAttributes}
              >
                スレッドを開く（{thread.replyCount}レス）
              </CatalogThreadLink>
              <BoardPost
                post={thread.opPost}
                isOp
                isR18Thread={isR18Thread}
                showR18={showR18}
              />
              {tails.get(thread.id)?.posts.map((post) => (
                <BoardPost
                  key={post.id}
                  post={post}
                  isR18Thread={isR18Thread}
                  showR18={showR18}
                />
              ))}
            </article>
          );
        })
      )}
    </>
  );
};

interface BoardPostProps {
  post: Post | ThreadChunkPost;
  isOp?: boolean;
  isR18Thread: boolean;
  showR18: boolean;
}

export const BoardPost: React.FunctionComponent<BoardPostProps> = ({
  post,
  isOp = false,
  isR18Thread,
  showR18,
}: BoardPostProps) => {
  const attachments = useMemo(() => getPostAttachments(post), [post]);
  const attachmentIdentities = useMemo(
    () =>
      attachments.map((attachment) =>
        getPostAttachmentIdentity(post.id, attachment),
      ),
    [attachments, post.id],
  );
  const [revealedAttachmentIds, setRevealedAttachmentIds] = useState<
    Set<string>
  >(() => new Set());
  const postContentRef = useRef<HTMLDivElement>(null);
  const displayNo = postNo(post);

  useEffect(() => {
    setRevealedAttachmentIds((current) =>
      reconcileAttachmentIdentities(current, attachmentIdentities),
    );
  }, [attachmentIdentities]);

  useEffect(() => {
    if (revealedAttachmentIds.size > 0) postContentRef.current?.focus();
  }, [revealedAttachmentIds]);

  return (
    <article className={isOp ? "board-post board-post-op" : "board-post"}>
      {attachments.map((attachment, index) => {
        const attachmentIdentity = attachmentIdentities[index] ?? "";
        const isR18Hidden = isR18ImageHidden({
          hasAttachment: true,
          isR18Thread,
          showR18,
          revealed: revealedAttachmentIds.has(attachmentIdentity),
        });
        const orderLabel =
          attachments.length > 1
            ? `（${index + 1}/${attachments.length}）`
            : "";
        return (
          <figure
            key={attachmentIdentity}
            className="board-post-image"
            data-attachment-order={index + 1}
          >
            <img
              src={getImageUrl(attachment, false)}
              alt={`No.${displayNo}の添付${orderLabel}`}
              loading="lazy"
              decoding="async"
              className={isR18Hidden ? "r18-spoiler-image" : undefined}
            />
            {isR18Hidden && (
              <button
                type="button"
                className="board-r18-spoiler-trigger"
                aria-label={`No.${displayNo}のR18画像${orderLabel}を表示`}
                onClick={(): void =>
                  setRevealedAttachmentIds((current) =>
                    new Set(current).add(attachmentIdentity),
                  )
                }
              />
            )}
          </figure>
        );
      })}
      <div
        ref={postContentRef}
        className="board-post-content"
        tabIndex={revealedAttachmentIds.size > 0 ? -1 : undefined}
      >
        <header className="board-post-meta">
          {isOp ? "OP " : ""}No.{displayNo}
        </header>
        <p>{getPostPlainBody(post)}</p>
      </div>
    </article>
  );
};
